// The share-card punchline bank (sprint 28 item 2, roadmap 59). Written by the designer, imported by the card builder.
// Plain data plus one picker. Each line is the big first line of a card, so it is short, confident and ours: it never
// names a wallet, SOL, a price or a prize, never quotes anyone, and never claims more than points (the card shows the
// player's score or streak itself). "Dave" is the studio's own running joke, not a player.
// Outcomes: what happened at the door, from the player's side.
//   rightOpen  opened to a person, was right        rightShut  kept it shut on a bot, was right
//   wrongOpen  opened and it was the wrong one      wrongShut  kept it shut and it was a person
//   timeout    the clock ran out                    shiftEnd   end of a shift
//   practiceEnd end of a practice run, call unknown    firstMinute the first-minute reveal
//   practiceRight / practiceWrong  end of a practice run after a right / wrong call
//   envelope   an envelope slid under the door (sprint 28 item 3; points and cosmetics only)
export const PUNCHLINES = {
  rightOpen: [
    'I opened the door. A real one.',
    'Somebody was in there. I knew it.',
    'Human. I could tell by the typos.',
    'I let them in. They earned it.',
    'The door opened and I was right.',
    'A real person, and I saw it first.',
    'Opened it. No regrets.',
    'I read the room. The room was a person.',
    'They knocked like they meant it.',
    'Good ear. Good call. Door open.',
    'I trusted my gut. My gut was a person.',
    'Not a bot. Not tonight.',
  ],
  rightShut: [
    'I kept the door shut. Good.',
    'Nice try, robot.',
    'Too smooth. Door stayed shut.',
    'It was a bot and I knew it.',
    'No one was home. Nothing was home.',
    'The door stayed shut. So did my doubt.',
    'It almost had me. Almost.',
    'I smelled a script. Shut.',
    'Polite, quick and wrong. Shut.',
    'A machine knocked. I did not answer.',
    'Too perfect to be a person.',
    'I read it once and shut it.',
  ],
  wrongOpen: [
    'I opened the door. It was not Dave.',
    'I opened the door. It was a bot.',
    'I let a machine in. Charming, though.',
    'It said all the right things. All of them.',
    'That was not a person. I opened anyway.',
    'I got played by a very nice robot.',
    'The door opened. So did my mistake.',
    'It knocked like a human. It lied.',
    'Wrong call. Great manners, though.',
    'I trusted it. That was the bug.',
    'Fooled by a polite little program.',
    'I should have asked one more question.',
  ],
  wrongShut: [
    'I shut the door on a real person.',
    'They were human and I slammed it.',
    'Sorry. You were real.',
    'I kept it shut. I should not have.',
    'A person stood out there. I said no.',
    'Wrong call. They were not a bot.',
    'Too suspicious. Too wrong.',
    'I called a human a robot.',
    'I thought it was a script. It was a someone.',
    'The door stayed shut on the wrong knock.',
    'I overthought a real person.',
    'That typo was real. I missed it.',
  ],
  timeout: [
    'I stared at the door. The clock won.',
    'I took too long. The door did not wait.',
    'Time ran out. The knock is still there.',
    'I could not decide. So I did not.',
    'The clock decided for me.',
    'I froze at the door.',
    'Still thinking. Too late.',
    'No call, no door, no answer.',
    'I hesitated. The door noticed.',
    'The timer won this one.',
    'Overthought it, again.',
    'One more question and I ran out of time.',
  ],
  shiftEnd: [
    'Shift over. The door is still shut.',
    'I worked the door all shift.',
    'End of shift. Ask me who was real.',
    'I kept the door all shift.',
    'The shift ended. My streak did not blink.',
    'A whole shift of knocks. I am still here.',
    'Closed up for the night. Nobody bad got in.',
    'Last knock answered. Shift done.',
    'The night shift is done. The door remembers.',
    'I listened. I decided. Shift over.',
    'Punched out. Doorkeeper off duty.',
    'Another shift, another set of liars.',
  ],
  // practiceEnd claims neither a win nor a loss: it is used when the call is not known (a timeout, no verdict)
  practiceEnd: [
    'Practice done. The real door is next.',
    'I trained on the fake door. Now the real one.',
    'Practice run over. Time to knock for real.',
    'Warm-up finished. The door is waiting.',
    'Practice makes a better doorkeeper.',
    'No stakes, and I still sweated.',
    'I practised spotting bots. They practised too.',
    'Free reps at the door. Done.',
    'Practice door closed. Come knock for real.',
    'Learned the tells. Now to use them.',
    'Practice is over. The real door bites, I hear.',
    'Dry run done. The real one bites.',
  ],
  // practiceRight and practiceWrong: the end of practice, told from the call that was made (sprint 30)
  practiceRight: [
    'Practice call: right.',
    'I read the practice door right.',
    'Right call, even in practice.',
    'I knew the practice door. Next one is for real.',
    'Practice done and the call was right.',
    'One practice door, one right call.',
    'I trusted my ear in practice. It held.',
    'Practice makes a doorkeeper. That one was right.',
    'Learned the tells, then got one right.',
    'Free door, right call. Come knock for real.',
    'Right on the practice door. The real one bites.',
    'Practice run over. I called it.',
    'Warm-up door: right.',
  ],
  practiceWrong: [
    'The practice door got me. Next time.',
    'Wrong call in practice. Good thing it is free.',
    'I missed one. Practice is for that.',
    'Practice door fooled me. I will learn.',
    'Wrong on the practice door. Lesson noted.',
    'Practice call: wrong. The tells are real.',
    'It fooled me, and it was only practice.',
    'I got that one wrong. Back to the tells.',
    'Missed it. The practice door is good.',
    'Wrong call, no stakes. I am learning.',
    'The free door beat me. I will be back.',
    'One wrong call in practice. Noted.',
    'Practice taught me something. The hard way.',
  ],
  firstMinute: [
    'One minute at the door. I am hooked.',
    'My first door. It was not what I expected.',
    'Sixty seconds and I judged a stranger.',
    'First knock, first call, first doubt.',
    'I just met the door. It met me back.',
    'Human or not. I had one minute to say.',
    'Day one at the door.',
    'I had no idea what was knocking.',
    'My first call at the door.',
    'It looked human. I had to decide.',
    'New doorkeeper. First knock.',
    'One minute in and I am suspicious of everyone.',
  ],
  envelope: [
    'An envelope slid under the door.',
    'Something came under the door.',
    'The door gave me a gift. Points only.',
    'A right call, and a letter at my feet.',
    'I opened the envelope. Slowly.',
    'Peeked through the hole first.',
    'The door sends small envelopes. I will take them.',
    'The slow reveal got me.',
    'An envelope for a good call.',
    'I got mail from the door.',
    'Right call. Then an envelope.',
    'The envelope is open. What a moment.',
  ],
};

export const OUTCOMES = Object.keys(PUNCHLINES);
export const REQUIRED_LINE = 'I opened the door. It was not Dave.';

// Never on a card: money, wallet talk, a person's words. Used by the bank test and by the card builder as a last guard.
export const FORBIDDEN = /\b(sol|solana|wallet|price|prices|usd|dollars?|money|prize|prizes|jackpot|pump|moon|airdrop|token|tokens|buy|sell)\b|[$€£]|0x[0-9a-f]{6,}|\b[1-9A-HJ-NP-Za-km-z]{32,44}\b|\d+(\.\d+)?\s*(sol|usd)\b/i;
export const isCardSafe = line => typeof line === 'string' && line.length > 0 && !FORBIDDEN.test(line);

// pick(outcome, { last, rand }): a punchline for the outcome, never the same as `last` (the previous one shown for it).
// rand is injectable (defaults to Math.random; this is cosmetic only, never a game roll).
export function pickPunchline(outcome, { last = null, rand = Math.random } = {}) {
  const bank = PUNCHLINES[outcome];
  if (!bank) throw new Error(`no punchline bank for outcome: ${outcome}`);
  const pool = bank.filter(l => l !== last);
  return pool[Math.min(pool.length - 1, Math.floor(rand() * pool.length))];
}
